import { useState } from 'react';
import { format } from 'date-fns';
import { ExternalLink, PenTool, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useGleifSearchByName } from '@/hooks/use-gleif';
import type { GleifRecordData } from '@/hooks/use-gleif';
import { cn } from '@/lib/utils';
import type { GleifCheck } from '../types';

interface GleifCheckProps {
  item: GleifCheck;
  onVerify: (id: string) => void;
  onSelectNameSearchResult: (id: string, record: GleifRecordData) => void;
  onReset: (id: string) => void;
  onSetNotApplicable: (id: string, notApplicable: boolean) => void;
  onSign: (id: string, role: 'creator' | 'reviewer') => void;
  onUpdateEntityName: (id: string, entityName: string) => void;
  onUpdateLei: (id: string, lei: string) => void;
  isSearching: boolean;
  userId: string | undefined;
}

function formatDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch {
    return value;
  }
}

function renderAddress(lines?: string[]): string {
  if (!lines || lines.length === 0) {
    return 'N/A';
  }
  return lines.filter(Boolean).join(', ');
}

export function GleifCheckComponent({
  item,
  onVerify,
  onSelectNameSearchResult,
  onReset,
  onSetNotApplicable,
  onSign,
  onUpdateEntityName,
  onUpdateLei,
  isSearching,
  userId,
}: GleifCheckProps) {
  const [nameSearchResults, setNameSearchResults] = useState<GleifRecordData[]>([]);
  const { mutateAsync: searchByName, isPending: isSearchingByName } = useGleifSearchByName();

  const handleNameSearch = async () => {
    if (!item.entityName) {
      toast.error('Enter an entity name before searching');
      return;
    }
    try {
      const data = await searchByName(item.entityName);
      if (data.results.length === 0) {
        toast.info('No GLEIF records found for this entity name');
        setNameSearchResults([]);
      } else {
        setNameSearchResults(data.results);
        toast.success(`Found ${data.results.length} result${data.results.length === 1 ? '' : 's'}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to search GLEIF by name';
      toast.error(message);
    }
  };

  const handleSelectResult = (record: GleifRecordData) => {
    onSelectNameSearchResult(item.id, record);
    setNameSearchResults([]);
  };

  const record = item.searchResults && typeof item.searchResults !== 'string' ? item.searchResults : null;
  const attributes = record?.data?.attributes;
  const legalName = attributes?.entity.legalName?.name;
  const legalAddress = attributes?.entity.legalAddress;
  const headquartersAddress = attributes?.entity.headquartersAddress;
  const registration = attributes?.registration;
  const events = attributes?.entity.eventGroups?.flatMap((group) => group.events ?? []) ?? [];
  const gleifRecordUrl = item.lei
    ? `https://search.gleif.org/#/record/${item.lei}`
    : record?.data?.links?.self;
  const conformityFlag = attributes?.conformityFlag;
  const isConforming = conformityFlag?.toUpperCase() === 'CONFORMING';
  const conformityLabel = conformityFlag
    ? (isConforming ? 'Conforming' : conformityFlag.replaceAll('_', ' '))
    : 'Unknown';
  const isDisabled = Boolean(item.notApplicable);
  const isBusy = isSearching || isSearchingByName;
  const canReset = item.lei || item.searchResults || item.verified;

  const handleReset = () => {
    onReset(item.id);
    setNameSearchResults([]);
  };

  return (
    <div className="space-y-4">
      {/* Stacked inputs */}
      <div className="space-y-3">
        <Input
          value={item.entityName}
          onChange={(e) => onUpdateEntityName(item.id, e.target.value)}
          placeholder="Enter entity name"
          disabled={isDisabled}
        />
        <Input
          value={item.lei}
          onChange={(e) => onUpdateLei(item.id, e.target.value.toUpperCase())}
          placeholder="Enter LEI (20 characters)"
          maxLength={20}
          disabled={isDisabled}
        />
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={handleNameSearch}
          disabled={isDisabled || !item.entityName || isBusy}
        >
          <Search className="size-4" />
          Name
        </Button>
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={() => onVerify(item.id)}
          disabled={isDisabled || !item.lei || isBusy}
        >
          {item.verified
            ? <RotateCcw className="size-4" />
            : <Search className="size-4" />}
          LEI
        </Button>
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            if (isBusy)
              return;
            onSetNotApplicable(item.id, !isDisabled);
          }}
          onKeyDown={(e) => {
            if (isBusy)
              return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSetNotApplicable(item.id, !isDisabled);
            }
          }}
          className={cn(
            'flex h-9 flex-1 select-none items-center justify-center gap-2 rounded-md border bg-muted/40 px-3',
            'cursor-pointer transition-colors hover:bg-muted/60',
            isBusy && 'cursor-not-allowed opacity-50 hover:bg-muted/40',
          )}
          aria-disabled={isBusy}
        >
          <Checkbox
            checked={isDisabled}
            onCheckedChange={(checked) => onSetNotApplicable(item.id, checked === true)}
            disabled={isBusy}
            id={`gleif-na-${item.id}`}
          />
          <span className="text-sm font-medium leading-none">
            N/A
          </span>
        </div>
        {canReset && (
          <Button
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={handleReset}
            disabled={isDisabled || isBusy}
            title="Reset LEI and verification"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>

      {/* Name search results */}
      {nameSearchResults.length > 0 && (
        <div className="space-y-2 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">
              Select a result (
              {nameSearchResults.length}
              {' '}
              found)
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => setNameSearchResults([])}
            >
              <X className="size-3" />
            </Button>
          </div>
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {nameSearchResults.map((result) => (
              <button
                key={result.id}
                type="button"
                onClick={() => handleSelectResult(result)}
                className="w-full rounded-md border bg-background p-3 text-left transition-colors hover:bg-muted"
              >
                <div className="font-medium">
                  {result.attributes.entity.legalName?.name || 'Unknown Entity'}
                </div>
                <div className="text-sm text-muted-foreground">
                  LEI:
                  {' '}
                  {result.attributes.lei}
                </div>
                {result.attributes.entity.legalAddress && (
                  <div className="text-sm text-muted-foreground">
                    {[
                      result.attributes.entity.legalAddress.city,
                      result.attributes.entity.legalAddress.region,
                      result.attributes.entity.legalAddress.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
                <div className="mt-1 text-xs text-muted-foreground">
                  Status:
                  {' '}
                  {result.attributes.entity.status || 'Unknown'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {isDisabled && (
        <div className="rounded-lg border bg-muted p-4 text-sm text-muted-foreground">
          Marked as N/A. This GLEIF check is disabled.
        </div>
      )}

      {!isDisabled && item.searchResults && (
        typeof item.searchResults === 'string'
          ? (
              <div className="rounded-lg border bg-muted p-4 text-sm">
                {item.searchResults}
              </div>
            )
          : (
              <div className="space-y-4 rounded-lg border p-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-muted-foreground">
                    LEI:
                    {' '}
                    <span className="font-semibold text-foreground">{attributes?.lei || 'Unknown'}</span>
                  </div>
                  <div className="text-lg font-semibold">
                    {legalName || 'Unknown Entity'}
                  </div>
                  {gleifRecordUrl && (
                    <a
                      href={gleifRecordUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View on GLEIF
                      <ExternalLink className="ml-1 inline-block size-3" />
                    </a>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">Legal Address</div>
                    <div className="text-muted-foreground">
                      {renderAddress(legalAddress?.addressLines)}
                    </div>
                    <div className="text-muted-foreground">
                      {[
                        legalAddress?.city,
                        legalAddress?.region,
                        legalAddress?.country,
                        legalAddress?.postalCode,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'N/A'}
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">Headquarters Address</div>
                    <div className="text-muted-foreground">
                      {renderAddress(headquartersAddress?.addressLines)}
                    </div>
                    <div className="text-muted-foreground">
                      {[
                        headquartersAddress?.city,
                        headquartersAddress?.region,
                        headquartersAddress?.country,
                        headquartersAddress?.postalCode,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">Entity Status</div>
                    <div className="text-muted-foreground">
                      {attributes?.entity.status || 'Unknown'}
                    </div>
                    <div className="text-muted-foreground">
                      Created
                      {' '}
                      {formatDate(attributes?.entity.creationDate) || 'N/A'}
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">Registration Status</div>
                    <div className="text-muted-foreground">
                      {registration?.status || 'Unknown'}
                    </div>
                    <div className="text-muted-foreground">
                      Last Updated
                      {' '}
                      {formatDate(registration?.lastUpdateDate) || 'N/A'}
                    </div>
                    <div className="text-muted-foreground">
                      Next Renewal
                      {' '}
                      {formatDate(registration?.nextRenewalDate) || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  <div className="font-medium">Policy Conformance</div>
                  <div className={cn(
                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
                    isConforming ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800',
                  )}
                  >
                    {conformityLabel}
                  </div>
                </div>

                {(attributes?.bic?.length || attributes?.spglobal?.length) && (
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">Identifiers</div>
                    {attributes?.bic?.length && (
                      <div className="text-muted-foreground">
                        <span className="font-medium text-foreground">BIC:</span>
                        {' '}
                        {attributes.bic.join(', ')}
                      </div>
                    )}
                    {attributes?.spglobal?.length && (
                      <div className="text-muted-foreground">
                        <span className="font-medium text-foreground">S&P Global:</span>
                        {' '}
                        {attributes.spglobal.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {events.length > 0 && (
                  <div className="space-y-2 text-sm">
                    <div className="font-medium">Recent Events</div>
                    <ul className="list-inside list-disc space-y-1 text-muted-foreground">
                      {events.slice(0, 5).map((event, idx) => {
                        const key = `${event.type ?? 'event'}-${event.recordedDate ?? event.effectiveDate ?? idx}`;
                        return (
                          <li key={key}>
                            <span className="font-medium text-foreground">{event.type || 'Event'}</span>
                            {event.effectiveDate && (
                              <>
                                {' '}
                                on
                                {' '}
                                {formatDate(event.effectiveDate)}
                              </>
                            )}
                            {event.status && ` · ${event.status}`}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )
      )}

      {item.verified && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={() => onSign(item.id, 'creator')}
          >
            {item.signatures.some((s) => s.userId === userId && s.role === 'creator')
              ? (
                  <>
                    <X className="size-4" />
                    Remove Verifier Signature
                  </>
                )
              : (
                  <>
                    <PenTool className="size-4" />
                    Sign as Verifier
                  </>
                )}
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={() => onSign(item.id, 'reviewer')}
          >
            {item.signatures.some((s) => s.userId === userId && s.role === 'reviewer')
              ? (
                  <>
                    <X className="size-4" />
                    Remove Reviewer Signature
                  </>
                )
              : (
                  <>
                    <PenTool className="size-4" />
                    Sign as Reviewer
                  </>
                )}
          </Button>
        </div>
      )}

      {(item.verifiedBy || item.signatures.length > 0) && (
        <div>
          {item.verifiedBy && (
            <div className="text-sm text-muted-foreground">
              {isDisabled ? 'Marked N/A' : 'Verified'}
              {' '}
              by
              {' '}
              {item.verifiedBy.name}
              {' '}
              on
              {' '}
              {format(new Date(item.verifiedBy.timestamp), 'MMM d, yyyy h:mm a')}
            </div>
          )}
          {item.signatures.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Signatures:</div>
              {item.signatures.map((signature) => (
                <div
                  key={`${signature.userId}-${signature.role}-${signature.timestamp}`}
                  className="text-sm text-muted-foreground"
                >
                  {signature.name}
                  {' '}
                  (
                  {signature.role}
                  )
                  {' — '}
                  {format(new Date(signature.timestamp), 'MMM d, yyyy h:mm a')}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
